import React, { useState } from "react";
import { Avatar, Box, Button, Chip, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import CloseRoundedIcon      from "@mui/icons-material/CloseRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import DragIndicatorRoundedIcon from "@mui/icons-material/DragIndicatorRounded";
import EmployeeAutocomplete from "../../../component/common/EmployeeAutocomplete";

/**
 * 결재선·열람자 — 왼쪽에서 고치고 오른쪽에서 완성된 라인을 본다.
 *
 * <h3>기본값을 두는 이유</h3>
 * 예전엔 상신자가 결재자를 맨손으로 골랐다. 그래서 같은 유형인데 단계 수가 제각각이었고
 * (운영 실측: 기타결재 27건은 1단계, 대금지급은 1·2·7단계가 뒤섞여 있었다),
 * 열람 기능이 없어서 <b>보게 하려고 결재선에 사람을 넣는</b> 일까지 벌어졌다.
 * 7단계짜리 지출 결재 한 건이 그 예인데 7명 중 6명이 실제로는 열람자였고,
 * 그 문서는 일주일째 1단계에 멈춰 있었다.
 *
 * 그래서 양식이 «보통은 이렇게» 를 채워 준다. 다만 예외는 늘 생기므로 잠그지는 않는다.
 *
 * <h3>본인은 결재자 후보에서 뺀다</h3>
 * 기안자가 자기 문서의 결재자로 들어가면 본인이 올리고 본인이 도장을 찍는 게 된다.
 * 실제로 대금지급 두 건이 그렇게 올라갔고 한 건은 <b>본인 승인만으로 확정 원장</b>까지 갔다.
 * 서버가 상신·승인 양쪽에서 막지만(<code>assertSubmitterNotInLine</code> /
 * <code>assertNotSelfApproval</code>), 400 을 보고 고치게 하는 것보다 <b>애초에 고를 수 없는</b> 쪽이 맞다.
 * 양식 기본값도 이미 «기안자 본인이라 건너뜁니다» 로 그 단계를 빼고 내려온다 — 화면이 그 규칙과 같아진다.
 *
 * <h3>최종 결재자 칸이 따로 있어도 어긋나지 않는 이유</h3>
 * 최종 결재자를 <b>별도 상태로 두지 않는다.</b> 결재자 목록 하나만 있고, 최종 결재자 칸은
 * 그 목록의 <b>마지막 자리</b>를 가리키는 창일 뿐이다. 그 칸에서 사람을 고르면 마지막 자리가
 * 바뀌고, 오른쪽에서 순서를 끌어 옮기면 새로 마지막이 된 사람이 그 칸에 뜬다.
 * 값이 둘이 아니므로 «마지막 결재자 ≠ 최종 승인자» 인 상태를 만들 방법이 없다.
 */
export default function ApprovalLineEditor({
    form, myInfo, approvers, viewers, onChangeApprovers, onChangeViewers, onReset,
}) {
    const skipped = (form?.defaultLine || []).filter((s) => s.skipped);

    const mid   = approvers.slice(0, -1);
    const final = approvers.length ? approvers[approvers.length - 1] : null;

    const setFinal = (person) => onChangeApprovers(person ? [...mid, person] : mid);
    const addMid   = (person) => onChangeApprovers([...mid, person, ...(final ? [final] : [])]);
    const removeAt = (id)     => onChangeApprovers(approvers.filter((x) => x.id !== id));

    const reorder = (from, to) => {
        if (from === to || from == null || to == null) return;
        const next = [...approvers];
        const [it] = next.splice(from, 1);
        next.splice(to, 0, it);
        onChangeApprovers(next);
    };

    const toPerson = (emp) => ({
        id: emp.id,
        name: emp.name,
        position: emp.position?.positionName ?? emp.positionName,
        deptName: emp.department?.deptName ?? emp.deptName,
    });

    return (
        <Box>
            <Stack direction="row" alignItems="center" sx={{ mt: 1, mb: 1 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155" }}>결재선</Typography>
                <Box sx={{ flex: 1 }} />
                <Button size="small" startIcon={<RestartAltRoundedIcon sx={{ fontSize: 15 }} />}
                        onClick={onReset} sx={{ fontSize: 11.5, color: "#64748b" }}>
                    기본값으로
                </Button>
            </Stack>

            <Stack direction="row" spacing={2} alignItems="flex-start">
                {/* ── 왼쪽: 고치는 곳 ── */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <FieldLabel>중간 결재자</FieldLabel>
                    {mid.length > 0 && (
                        <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mb: 0.75 }}>
                            {mid.map((p, i) => (
                                <Chip
                                    key={p.id} size="small"
                                    avatar={<Avatar sx={{ bgcolor: "#2563eb", fontSize: 10 }}>{(p.name || "?")[0]}</Avatar>}
                                    label={`${i + 1}. ${p.name}${p.deptName ? ` · ${p.deptName}` : ""}`}
                                    onDelete={() => removeAt(p.id)}
                                    sx={{ fontSize: 11.5, bgcolor: "#eff6ff", color: "#1e40af" }}
                                />
                            ))}
                        </Stack>
                    )}
                    <EmployeeAutocomplete
                        value={null}
                        onChange={(emp) => {
                            if (!emp || typeof emp === "string") return;
                            if (approvers.some((x) => x.id === emp.id)) return;
                            addMid(toPerson(emp));
                        }}
                        excludeIds={approvers.map((x) => x.id)}
                        excludeSelf selfId={myInfo?.id}
                        placeholder="중간 결재자 추가 (순서대로 쌓입니다)"
                        variant="outlined"
                        disableUnderline={false}
                    />

                    <FieldLabel sx={{ mt: 2 }}>최종 결재자</FieldLabel>
                    <EmployeeAutocomplete
                        value={final}
                        onChange={(emp) => {
                            if (!emp || typeof emp === "string") { setFinal(null); return; }
                            if (mid.some((x) => x.id === emp.id)) return;   // 중간 결재자와 겹치면 무시
                            setFinal(toPerson(emp));
                        }}
                        excludeIds={mid.map((x) => x.id)}
                        excludeSelf selfId={myInfo?.id}
                        placeholder="최종 결재자 선택"
                        variant="outlined"
                        disableUnderline={false}
                    />
                    {!final && (
                        <Typography sx={{ fontSize: 11.5, color: "#b45309", mt: 0.5 }}>
                            최종 결재자가 없습니다. 한 명을 지정해 주세요.
                        </Typography>
                    )}

                    {skipped.map((s) => (
                        <Typography key={s.stepOrder} sx={{ fontSize: 11, color: "#94a3b8", mt: 0.5 }}>
                            · 기본값 {s.stepOrder}단계 건너뜀 — {s.skipReason}
                        </Typography>
                    ))}

                    <FieldLabel sx={{ mt: 2 }}>열람자</FieldLabel>
                    {viewers.length === 0 ? (
                        <Typography sx={{ fontSize: 12, color: "#94a3b8", mb: 0.75 }}>지정된 열람자가 없습니다.</Typography>
                    ) : (
                        <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mb: 0.75 }}>
                            {viewers.map((v) => {
                                // 서버는 결재자와 겹치는 열람자를 빼고 저장한다(같은 문서가 결재함·열람함에
                                // 두 번 뜨는 걸 막으려고). 화면이 안 보여주면 올린 뒤 조용히 사라진 것처럼 보인다.
                                const isApprover = approvers.some((a) => a.id === v.employeeId);
                                return (
                                    <Tooltip key={v.employeeId}
                                             title={isApprover ? "결재자로 지정돼 열람자에서는 빠집니다" : ""}
                                             placement="top">
                                        <Chip
                                            size="small"
                                            label={`${v.name}${v.deptName ? ` · ${v.deptName}` : ""}`}
                                            onDelete={() => onChangeViewers(viewers.filter((x) => x.employeeId !== v.employeeId))}
                                            sx={{ fontSize: 11, bgcolor: "#f1f5f9",
                                                  opacity: isApprover ? 0.5 : 1,
                                                  textDecoration: isApprover ? "line-through" : "none" }}
                                        />
                                    </Tooltip>
                                );
                            })}
                        </Stack>
                    )}
                    <EmployeeAutocomplete
                        value={null}
                        onChange={(emp) => {
                            if (!emp || typeof emp === "string") return;
                            if (viewers.some((x) => x.employeeId === emp.id)) return;
                            onChangeViewers([...viewers, {
                                employeeId: emp.id, name: emp.name,
                                deptName: emp.department?.deptName ?? emp.deptName,
                            }]);
                        }}
                        excludeIds={[...viewers.map((x) => x.employeeId), ...approvers.map((a) => a.id)]}
                        placeholder="열람자 추가"
                        variant="outlined"
                        disableUnderline={false}
                    />
                    <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.75 }}>
                        열람자는 결재하지 않고 문서를 보고 댓글만 남깁니다.
                    </Typography>
                </Box>

                {/* ── 오른쪽: 완성된 라인 ── */}
                <LinePreview
                    submitter={myInfo}
                    approvers={approvers}
                    onReorder={reorder}
                    onRemove={removeAt}
                />
            </Stack>
        </Box>
    );
}

/**
 * 실제로 만들어질 결재 라인 — 상세 화면과 같은 세로 타임라인.
 *
 * 끌어서 순서를 바꿀 수 있다. 맨 아래로 옮긴 사람이 최종 결재자가 된다 —
 * 왼쪽 «최종 결재자» 칸도 같이 바뀐다. 값이 하나뿐이라 따로 맞출 게 없다.
 */
function LinePreview({ submitter, approvers, onReorder, onRemove }) {
    const [dragIdx, setDragIdx] = useState(null);
    const [overIdx, setOverIdx] = useState(null);

    return (
        <Box sx={{ width: 268, flexShrink: 0, border: "1px solid #e2e8f0",
                   borderRadius: 2, overflow: "hidden", alignSelf: "stretch" }}>
            <Box sx={{ px: 1.5, py: 1, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#334155" }}>결재 라인</Typography>
            </Box>

            <Box sx={{ p: 1.5 }}>
                <Row avatarBg="#2563eb" initial={(submitter?.name || "?")[0]}
                     name={submitter?.name || "나"}
                     sub={[submitter?.positionName ?? submitter?.position?.positionName,
                           submitter?.deptName ?? submitter?.department?.deptName].filter(Boolean).join(" · ")}
                     state="신청" stateColor="#2563eb" connect={approvers.length > 0} />

                {approvers.map((p, i) => {
                    const last = i === approvers.length - 1;
                    return (
                        <Box
                            key={p.id}
                            draggable
                            onDragStart={() => setDragIdx(i)}
                            onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
                            onDragOver={(e) => { e.preventDefault(); setOverIdx(i); }}
                            onDrop={(e) => { e.preventDefault(); onReorder(dragIdx, i); setDragIdx(null); setOverIdx(null); }}
                            sx={{
                                cursor: "grab",
                                borderRadius: 1,
                                opacity: dragIdx === i ? 0.4 : 1,
                                bgcolor: overIdx === i && dragIdx !== i ? "#eff6ff" : "transparent",
                                "&:hover .drag-handle": { opacity: 1 },
                            }}>
                            <Row
                                avatarBg="#94a3b8" initial={(p.name || "?")[0]}
                                name={p.name}
                                sub={[p.position, p.deptName].filter(Boolean).join(" · ")}
                                state={`${i + 1}단계 결재`} stateColor="#64748b"
                                connect={!last}
                                badge={last ? "최종" : null}
                                onRemove={() => onRemove(p.id)}
                            />
                        </Box>
                    );
                })}

                {approvers.length === 0 && (
                    <Typography sx={{ fontSize: 11.5, color: "#b45309", mt: 1 }}>
                        결재자가 없어 상신할 수 없습니다.
                    </Typography>
                )}

                {approvers.length > 1 && (
                    <Typography sx={{ fontSize: 10.5, color: "#94a3b8", mt: 1.5 }}>
                        끌어서 순서를 바꿀 수 있습니다. 맨 아래가 최종 결재자입니다.
                    </Typography>
                )}
            </Box>
        </Box>
    );
}

function Row({ avatarBg, initial, name, sub, state, stateColor, connect, badge, onRemove }) {
    return (
        <Stack direction="row" spacing={1.25} alignItems="flex-start">
            <Stack alignItems="center" sx={{ flexShrink: 0 }}>
                <Avatar sx={{ width: 30, height: 30, fontSize: 12, bgcolor: avatarBg }}>{initial}</Avatar>
                {connect && <Box sx={{ width: 2, flex: 1, minHeight: 22, bgcolor: "#dbeafe", my: 0.4 }} />}
            </Stack>

            <Box sx={{ flex: 1, minWidth: 0, pb: connect ? 0 : 0.5 }}>
                <Stack direction="row" alignItems="center" spacing={0.6}>
                    {/* 이름이 줄 맨 앞에 오도록 손잡이는 칩 뒤로 뺀다.
                        앞에 두면 사람마다 이름 시작 위치가 어긋나 목록이 들쭉날쭉해 보인다. */}
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }} noWrap>{name}</Typography>
                    {badge && (
                        <Chip size="small" label={badge}
                              sx={{ height: 17, fontSize: 10, fontWeight: 800,
                                    bgcolor: "#fef3c7", color: "#b45309" }} />
                    )}
                    {onRemove && (
                        <DragIndicatorRoundedIcon className="drag-handle"
                            sx={{ fontSize: 14, color: "#cbd5e1", opacity: 0, transition: "opacity 120ms" }} />
                    )}
                    <Box sx={{ flex: 1 }} />
                    {onRemove && (
                        <IconButton size="small" sx={{ p: 0.15, color: "#cbd5e1" }} onClick={onRemove}>
                            <CloseRoundedIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                    )}
                </Stack>
                {sub && <Typography sx={{ fontSize: 11, color: "#94a3b8" }} noWrap>{sub}</Typography>}
                <Typography sx={{ fontSize: 11.5, color: stateColor, fontWeight: 600, mt: 0.15 }}>
                    ● {state}
                </Typography>
            </Box>
        </Stack>
    );
}

function FieldLabel({ children, sx }) {
    return (
        <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#475569", mb: 0.6, ...sx }}>
            {children}
        </Typography>
    );
}
